import { isFocusVisible, useHover, usePress } from "@react-aria/interactions";
import { getItemCount } from "@react-stately/collections";
import { useSelectableList, useSelectableItem } from "@react-aria/selection";
import _babelRuntimeHelpersEsmObjectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { useOverlayTrigger } from "@react-aria/overlays";
import { useId, filterDOMProps, mergeProps, useSlotId } from "@react-aria/utils";
import _babelRuntimeHelpersEsmExtends from "@babel/runtime/helpers/esm/extends";
/**
 * Provides the behavior and accessibility implementation for a menu trigger.
 * @param props - Props for the menu trigger.
 * @param state - State for the menu trigger.
 */

export function useMenuTrigger(props, state, ref) {
  var _props$type = props.type,
      type = _props$type === void 0 ? 'menu' : _props$type,
      isDisabled = props.isDisabled;
  var menuTriggerId = useId();

  var _useOverlayTrigger = useOverlayTrigger({
    type: type
  }, state, ref),
      triggerProps = _useOverlayTrigger.triggerProps,
      overlayProps = _useOverlayTrigger.overlayProps;

  var onKeyDown = function onKeyDown(e) {
    if (isDisabled) {
      return;
    }

    if (ref && ref.current) {
      switch (e.key) {
        case 'ArrowDown':
        case 'Enter':
        case ' ':
          // Stop propagation, unless it would already be handled by useKeyboard.
          if (!('continuePropagation' in e)) {
            e.stopPropagation();
          }

          e.preventDefault();
          state.toggle('first');
          break;

        case 'ArrowUp':
          if (!('continuePropagation' in e)) {
            e.stopPropagation();
          }

          e.preventDefault();
          state.toggle('last');
          break;
      }
    }
  };

  return {
    menuTriggerProps: _babelRuntimeHelpersEsmExtends({}, triggerProps, {
      id: menuTriggerId,
      onPressStart: function onPressStart(e) {
        // For consistency with native, open the menu on mouse/key down, but touch up.
        if (e.pointerType !== 'touch' && e.pointerType !== 'keyboard' && !isDisabled) {
          // If opened with a screen reader, auto focus the first item.
          // Otherwise, the menu itself will be focused.
          state.toggle(e.pointerType === 'virtual' ? 'first' : null);
        }
      },
      onPress: function onPress(e) {
        if (e.pointerType === 'touch' && !isDisabled) {
          state.toggle();
        }
      },
      onKeyDown: onKeyDown
    }),
    menuProps: _babelRuntimeHelpersEsmExtends({}, overlayProps, {
      'aria-labelledby': menuTriggerId
    })
  };
}
/**
 * Provides the behavior and accessibility implementation for a menu component.
 * A menu displays a list of actions or options that a user can choose.
 * @param props - Props for the menu.
 * @param state - State for the menu, as returned by `useListState`.
 */

export function useMenu(props, state, ref) {
  var _props$shouldFocusWra = props.shouldFocusWrap,
      shouldFocusWrap = _props$shouldFocusWra === void 0 ? true : _props$shouldFocusWra,
      otherProps = _babelRuntimeHelpersEsmObjectWithoutPropertiesLoose(props, ["shouldFocusWrap"]);

  if (!props['aria-label'] && !props['aria-labelledby']) {
    console.warn('An aria-label or aria-labelledby prop is required for accessibility.');
  }

  var domProps = filterDOMProps(props, {
    labelable: true
  });

  var _useSelectableList = useSelectableList(_babelRuntimeHelpersEsmExtends({}, otherProps, {
    ref: ref,
    selectionManager: state.selectionManager,
    collection: state.collection,
    disabledKeys: state.disabledKeys,
    shouldFocusWrap: shouldFocusWrap
  })),
      listProps = _useSelectableList.listProps;

  return {
    menuProps: mergeProps(domProps, _babelRuntimeHelpersEsmExtends({
      role: 'menu'
    }, listProps))
  };
}
/**
 * Provides the behavior and accessibility implementation for an item in a menu.
 * See `useMenu` for more details about menus.
 * @param props - Props for the item.
 * @param state - State for the menu, as returned by `useTreeState`.
 */

export function useMenuItem(props, state, ref) {
  var isSelected = props.isSelected,
      isDisabled = props.isDisabled,
      key = props.key,
      onClose = props.onClose,
      closeOnSelect = props.closeOnSelect,
      isVirtualized = props.isVirtualized,
      onAction = props.onAction;
  var role = 'menuitem';

  if (state.selectionManager.selectionMode === 'single') {
    role = 'menuitemradio';
  } else if (state.selectionManager.selectionMode === 'multiple') {
    role = 'menuitemcheckbox';
  }

  var labelId = useSlotId();
  var descriptionId = useSlotId();
  var keyboardId = useSlotId();
  var ariaProps = {
    'aria-disabled': isDisabled,
    role: role,
    'aria-label': props['aria-label'],
    'aria-labelledby': labelId,
    'aria-describedby': [descriptionId, keyboardId].filter(Boolean).join(' ') || undefined
  };

  if (state.selectionManager.selectionMode !== 'none') {
    ariaProps['aria-checked'] = isSelected;
  }

  if (isVirtualized) {
    ariaProps['aria-posinset'] = state.collection.getItem(key).index;
    ariaProps['aria-setsize'] = getItemCount(state.collection);
  }

  var onKeyDown = function onKeyDown(e) {
    // Ignore repeating events, which may have started on the menu trigger before moving
    // focus to the menu item. We want to wait for a second complete key press sequence.
    if (e.repeat) {
      return;
    }

    switch (e.key) {
      case ' ':
        if (!isDisabled && state.selectionManager.selectionMode === 'none' && closeOnSelect !== false && onClose) {
          onClose();
        }

        break;

      case 'Enter':
        // The Enter key should always close on select, except if overridden.
        if (!isDisabled && closeOnSelect !== false && onClose) {
          onClose();
        }

        break;
    }
  };

  var onPressStart = function onPressStart(e) {
    if (e.pointerType === 'keyboard' && onAction) {
      onAction(key);
    }
  };

  var onPressUp = function onPressUp(e) {
    if (e.pointerType !== 'keyboard') {
      if (onAction) {
        onAction(key);
      } // Pressing a menu item should close by default in single selection mode but not multiple
      // selection mode, except if overridden by the closeOnSelect prop.


      if (onClose && (closeOnSelect != null ? closeOnSelect : state.selectionManager.selectionMode !== 'multiple')) {
        onClose();
      }
    }
  };

  var _useSelectableItem = useSelectableItem({
    selectionManager: state.selectionManager,
    key: key,
    ref: ref,
    shouldSelectOnPressUp: true
  }),
      itemProps = _useSelectableItem.itemProps;

  var _usePress = usePress({
    onPressStart: onPressStart,
    onPressUp: onPressUp,
    isDisabled: isDisabled
  }),
      pressProps = _usePress.pressProps;

  var _useHover = useHover({
    isDisabled: isDisabled,
    onHoverStart: function onHoverStart() {
      if (!isFocusVisible()) {
        state.selectionManager.setFocused(true);
        state.selectionManager.setFocusedKey(key);
      }
    }
  }),
      hoverProps = _useHover.hoverProps;

  return {
    menuItemProps: _babelRuntimeHelpersEsmExtends({}, ariaProps, mergeProps(itemProps, pressProps, hoverProps, {
      onKeyDown: onKeyDown
    })),
    labelProps: {
      id: labelId
    },
    descriptionProps: {
      id: descriptionId
    },
    keyboardShortcutProps: {
      id: keyboardId
    }
  };
}
/**
 * Provides the behavior and accessibility implementation for a section in a menu.
 * See `useMenu` for more details about menus.
 * @param props - Props for the section.
 */

export function useMenuSection(props) {
  var heading = props.heading,
      ariaLabel = props['aria-label'];
  var headingId = useId();
  return {
    itemProps: {
      role: 'presentation'
    },
    headingProps: heading ? {
      // Techincally, menus cannot contain headings according to ARIA.
      // We hide the heading from assistive technology, and only use it
      // as a label for the nested group.
      id: headingId,
      'aria-hidden': true
    } : {},
    groupProps: {
      role: 'group',
      'aria-label': ariaLabel,
      'aria-labelledby': heading ? headingId : undefined
    }
  };
}